/**
 * Report setup + Site Project Intent rows for the Coverage Matrix.
 *
 * Wizard typed preferences and canonical project intent are first-class
 * checklist requirements — prepended before document attestation rows.
 */
import type { ReportTypeId } from './reportTypes';
import type { ChecklistItem, ChecklistSeverity } from './documentChecklists';
import type { AuthoredFieldDef } from '../types/authoredEvidence';
import type { SedSectionId, SiteEvidenceDocument } from '../types/siteEvidence';
import type { ProjectIntent } from '../types/siteRag';
import { labelFor } from '../lib/reportPreferences/labels';
import { PROJECT_INTENT_ATTESTATION_DEFS } from '../lib/projectIntentAttestations';
import { resolveIntentPowerStack, resolveIntentPowerStackBand } from '../lib/powerStack';
// GAP 3 (field-architecture wiring) — the water-resource SED requirements gate
// on the cold-end heat-rejection SINK (the water-withdrawal axis), reusing the
// SAME shared set the cross-field guard + Path-to-GO gate use.
import { WATER_WITHDRAWING_SINKS } from '../lib/reportGeneration/reviewScoring/extractCrossFieldFindings';

/**
 * Facility-demand basis for firm-capacity screening. Use the canonical
 * ultimate phase / range and convert IT demand through PUE exactly once.
 * A range's sizing basis remains an estimate, not a declared exact load.
 */
export function firmCapacityBasisFromIntent(projectIntent?: ProjectIntent | null): number | undefined {
  return resolveIntentPowerStack(projectIntent ?? undefined).facilityMW
    ?? resolveIntentPowerStackBand(projectIntent)?.stacks.sizingBasis.facilityMW
    ?? undefined;
}

function activePhaseCount(projectIntent?: ProjectIntent | null): number {
  // The per-phase STATUS picker was removed from the Project Intent form, so
  // every entered phase counts as ACTIVE. Previously filtered
  // `p.status !== 'decommissioned'`.
  return (projectIntent?.phases ?? []).length;
}

function isHighWaterStressRegion(sed?: SiteEvidenceDocument | null): boolean {
  const ws = sed?.waterStress?.data;
  if (!ws) return false;
  return ws.stressLevel === 'High' || (ws.overallWaterRisk != null && ws.overallWaterRisk >= 4) || (ws.bwsScore != null && ws.bwsScore >= 4);
}

export type ChecklistItemKind = 'document' | 'report-setup' | 'site-intent';

export const SITE_PROJECT_INTENT_ID = 'site-project-intent';

/** Report types with a wizard preferences step (excludes public-sentiment). */
export const SETUP_ENABLED_REPORT_TYPES = [
  'feasibility',
  'supply-chain-risk',
  'environmental',
  'test-fit',
  'power-availability',
  'water-resource',
  'interconnection',
  'custom-brief',
  'financial',
  'executive-summary',
] as const satisfies readonly ReportTypeId[];

export type SetupEnabledReportType = (typeof SETUP_ENABLED_REPORT_TYPES)[number];

export function reportSetupRequirementId(reportType: ReportTypeId): string {
  return `${reportType}-report-setup`;
}

export function isSetupEnabledReportType(
  reportType: ReportTypeId,
): reportType is SetupEnabledReportType {
  return (SETUP_ENABLED_REPORT_TYPES as readonly ReportTypeId[]).includes(reportType);
}

export function setupReportTypeForRequirement(requirementId: string): ReportTypeId | undefined {
  if (requirementId === SITE_PROJECT_INTENT_ID) return undefined;
  if (!requirementId.endsWith('-report-setup')) return undefined;
  const typeId = requirementId.slice(0, -'-report-setup'.length) as ReportTypeId;
  return isSetupEnabledReportType(typeId) ? typeId : undefined;
}

export function isNonDocumentRequirement(item: ChecklistItem): boolean {
  return item.kind === 'site-intent' || item.kind === 'report-setup';
}

function field(
  reportType: ReportTypeId,
  key: string,
  type: AuthoredFieldDef['type'],
  extra?: Partial<AuthoredFieldDef>,
): AuthoredFieldDef {
  return { key, label: labelFor(reportType, key), type, ...extra };
}

function setupBase(
  id: string,
  label: string,
  description: string,
  sedSectionsHelpful: SedSectionId[],
  schema: readonly AuthoredFieldDef[],
  kind: ChecklistItemKind,
  severity: ChecklistSeverity = 'recommended',
): ChecklistItem {
  return {
    id,
    label,
    description,
    sedSectionsHelpful,
    relevanceTagPattern: [],
    severity,
    kind,
    authoredSchema: schema,
  };
}

const SITE_INTENT_SCHEMA: readonly AuthoredFieldDef[] = [
  {
    key: 'powerRequirement',
    label: 'Power requirement (bucket)',
    type: 'enum',
    required: true,
    enumValues: ['under-10mw', '10-50mw', '50-200mw', '200-500mw', '500-1000mw', '1000-1500mw', 'over-1500mw', 'over-200mw', 'undecided'],
  },
  { key: 'powerRequirementMW', label: 'Power requirement (MW)', type: 'number', unit: 'MW' },
  {
    key: 'workloadProfile',
    label: 'Workload profile',
    type: 'enum',
    enumValues: ['ai-training', 'ai-inference', 'hpc', 'enterprise', 'colocation', 'edge', 'mixed'],
  },
  {
    // P0-5 — demoted from `required:true`. `coolingStrategy` is the rack-side
    // IT loop (air vs liquid) — explicitly orthogonal to the BTM power/water
    // decision (coolingValidity: "ALWAYS viable"). The feasibility-decisive
    // cooling signals are the heat-rejection SINK (`bopHeatRejection`, drives
    // water withdrawal) and the cold-production MACHINE (`coolingGeneration`,
    // drives PUE / waste-heat), surfaced below as optional rows. Keeping
    // `coolingStrategy` required forced it (with `powerRequirement`) to be one
    // of only two fields the review ever showed.
    key: 'coolingStrategy',
    label: 'Cooling strategy (rack-side)',
    type: 'enum',
    enumValues: ['air', 'liquid', 'hybrid', 'undecided'],
  },
  // P0-7 — BTM cooling axes. ADDITIVE + optional display/read wiring for the two
  // feasibility-decisive cooling fields captured at gen time on
  // `projectIntent` but previously invisible in the review.
  //   - `bopHeatRejection`  — the cold-END heat-rejection SINK (drives water
  //     withdrawal: dry / wet / once-through / hybrid …).
  //   - `coolingGeneration` — the cold-PRODUCTION MACHINE (drives PUE /
  //     waste-heat: electric vapour-compression / absorption-thermal …).
  // Labelled via HEAT_REJECTION_LABELS / COOLING_GENERATION_LABELS in
  // `readSiteIntentValue` (resolveReportSetupFields.ts).
  {
    key: 'bopHeatRejection',
    label: 'Heat-rejection sink',
    type: 'enum',
    enumValues: [
      'dry-cooler',
      'wet-cooling-tower',
      'hybrid',
      'once-through',
      'absorption-cchp',
      'free-cooling',
      'unknown',
    ],
  },
  {
    key: 'coolingGeneration',
    label: 'Cooling generation',
    type: 'enum',
    enumValues: [
      'absorption-thermal',
      'vapour-compression-electric',
      'steam-driven-mechanical',
      'free-cooling-economiser',
      'none',
      'unknown',
    ],
  },
  {
    key: 'nuclearInterest',
    label: 'Nuclear interest',
    type: 'enum',
    enumValues: ['primary', 'considering', 'no', 'undecided'],
  },
  {
    key: 'priority',
    label: 'Priority',
    type: 'enum',
    enumValues: ['cost', 'speed', 'sustainability', 'resilience', 'undecided'],
  },
  { key: 'inServiceDate', label: 'In-service date', type: 'date' },
  // P0-7 — expanded profile (power-semantics basis + datacenter facts) already
  // captured on `projectIntent` at gen time; surfaced here (optional) so the
  // review can show what the model + scorer actually consume. Display/read only.
  {
    key: 'powerBasis',
    label: 'Power basis',
    type: 'enum',
    enumValues: ['it-load', 'facility'],
  },
  { key: 'pueValue', label: 'PUE', type: 'number' },
  { key: 'powerFactor', label: 'Power factor', type: 'number' },
  {
    key: 'annualDemandUtilizationRangePct.low',
    label: 'Annual data-center demand utilization — low',
    type: 'number',
    unit: '%',
  },
  {
    key: 'annualDemandUtilizationRangePct.high',
    label: 'Annual data-center demand utilization — high',
    type: 'number',
    unit: '%',
  },
  {
    key: 'datacenterTierTarget',
    label: 'Datacenter tier target',
    type: 'enum',
    enumValues: ['I', 'II', 'III', 'IV'],
  },
  {
    key: 'redundancyClass',
    label: 'Redundancy class',
    type: 'enum',
    enumValues: ['N', 'N+1', '2N', '2N+1'],
  },
  {
    key: 'carbonCommitment',
    label: 'Carbon commitment',
    type: 'enum',
    enumValues: ['none', 're100', '24-7-cfe', 'net-zero-2030', 'net-zero-2050', 'custom'],
  },
  // P1 (field-architecture audit §3.6/§4) — SIX net-new canonical BTM-feasibility
  // intent fields. ALL OPTIONAL display/read rows (no `required:true`) so they
  // surface in the now-ungated review without ever blocking generation. Nested
  // object fields use dotted keys, read + labelled in `readSiteIntentValue`
  // (resolveReportSetupFields.ts). See ProjectIntent.{interconnectionStatus,
  // fuelAccess, waterSource, landControl, offtake, capacityCreditIntent}.
  {
    key: 'interconnectionStatus.queueStatus',
    label: 'Utility service / interconnection status',
    type: 'enum',
    enumValues: ['none', 'pre-application', 'queued', 'study', 'executed-LGIA', 'executed-load-service'],
  },
  { key: 'interconnectionStatus.queueId', label: 'Utility application / queue ID', type: 'string' },
  {
    key: 'interconnectionStatus.studyPhase',
    label: 'Interconnection study phase',
    type: 'enum',
    enumValues: ['feasibility', 'system-impact', 'facilities'],
  },
  {
    key: 'fuelAccess.supply',
    label: 'Gas / fuel access',
    type: 'enum',
    enumValues: ['none', 'near-pipeline', 'lateral-required', 'firm-contracted'],
  },
  { key: 'fuelAccess.distanceKm', label: 'Pipeline distance', type: 'number', unit: 'km' },
  {
    key: 'waterSource.source',
    label: 'Water source',
    type: 'enum',
    enumValues: ['municipal', 'groundwater', 'surface', 'reuse', 'none'],
  },
  {
    key: 'waterSource.rightsStatus',
    label: 'Water rights status',
    type: 'enum',
    enumValues: ['none', 'applied', 'permitted'],
  },
  {
    key: 'offtake.structure',
    label: 'Offtake / PPA structure',
    type: 'enum',
    enumValues: ['merchant', 'contracted', 'hybrid'],
  },
  { key: 'offtake.tenorYears', label: 'PPA tenor', type: 'number', unit: 'yr' },
  { key: 'offtake.pricePerMwhUsd', label: 'PPA price', type: 'number', unit: '$/MWh' },
  {
    key: 'capacityCreditIntent.target',
    label: 'Capacity-credit (ELCC) intent',
    type: 'enum',
    enumValues: ['none', 'partial', 'firm'],
  },
  { key: 'capacityCreditIntent.targetMW', label: 'Firm-capacity target', type: 'number', unit: 'MW' },
  // R-131-EPISTEMIC-GROUNDING-E — operator attestations (Coverage Matrix row)
  ...PROJECT_INTENT_ATTESTATION_DEFS.map(def => ({
    key: `attestations.${def.key}`,
    label: def.label,
    type: 'enum' as const,
    enumValues: [...def.enumValues],
  })),
];

function siteIntentRow(): ChecklistItem {
  return setupBase(
    SITE_PROJECT_INTENT_ID,
    'Site Project Intent',
    'Canonical site-level inputs shared across report types. Edit in the Project Intent editor.',
    ['siteProfile', 'grid', 'electricity'],
    SITE_INTENT_SCHEMA,
    'site-intent',
  );
}

function testFitSetup(): ChecklistItem {
  const t = 'test-fit' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Test Fit report setup',
    'Target footprint, phasing, setbacks, parking, and stormwater targets for the layout analysis.',
    ['siteProfile', 'parcel'],
    [
      field(t, 'targetFootprintAcres', 'number', {
        required: true,
        unit: 'ac',
        derive: ({ site, projectIntent }) => {
          // Every entered phase is active (per-phase STATUS picker removed).
          const phaseAcres = projectIntent?.phases
            ?.reduce((acc, p) => acc + (p.areaAcres ?? 0), 0);
          if (phaseAcres && phaseAcres > 0) return phaseAcres;
          return site.siteArea ?? undefined;
        },
      }),
      field(t, 'phasing', 'enum', {
        enumValues: ['single', 'phased'],
        derive: ({ projectIntent }) => {
          const count = activePhaseCount(projectIntent);
          if (count > 1) return 'phased';
          if (count === 1) return 'single';
          return undefined;
        },
      }),
      field(t, 'phaseCount', 'number', {
        derive: ({ projectIntent }) => {
          const count = activePhaseCount(projectIntent);
          if (count >= 2) return count;
          if (count === 1) return 1;
          return undefined;
        },
      }),
      field(t, 'setbackOverrideFt', 'number', { unit: 'ft' }),
      field(t, 'parkingRatioPerEmployee', 'number'),
      field(t, 'stormwaterDetentionGal', 'number', { unit: 'gal' }),
    ],
    'report-setup',
  );
}

function environmentalSetup(): ChecklistItem {
  const t = 'environmental' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Environmental report setup',
    'NEPA pathway, air basin, discharge target, species, and tribal proximity flags.',
    ['environmental', 'regulatory'],
    [
      field(t, 'expectedNepaPathway', 'enum', {
        required: true,
        enumValues: ['catex', 'ea', 'eis', 'unknown'],
        derive: () => 'unknown',
      }),
      field(t, 'airBasinOverride', 'enum', {
        enumValues: ['attainment', 'non-attainment-marginal', 'non-attainment-serious', 'auto'],
        derive: () => 'auto',
      }),
      field(t, 'dischargeWaterTarget', 'enum', {
        enumValues: ['npdes', 'zld', 'reuse', 'unknown'],
      }),
      field(t, 'speciesConcerns', 'list'),
      field(t, 'tribalLandProximityKnown', 'boolean'),
      field(t, 'federalNexus', 'boolean'),
    ],
    'report-setup',
  );
}

function powerSetup(): ChecklistItem {
  const t = 'power-availability' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Power Availability report setup',
    'BTM strategy, renewables preference, curtailment tolerance, and substation thresholds.',
    ['grid', 'electricity'],
    [
      field(t, 'btmStrategy', 'enum', {
        required: true,
        enumValues: ['grid-only', 'btm-only', 'hybrid', 'islanded'],
        // Nuclear interest alone says nothing about a grid connection.
        derive: ({ site }) => site?.typedPreferencesDefaults?.['power-availability']?.btmStrategy,
      }),
      field(t, 'renewablesPreference', 'enum', {
        enumValues: ['required', 'preferred', 'agnostic', 'avoided'],
        derive: ({ projectIntent }) =>
          projectIntent?.priority === 'sustainability' ? 'preferred' : 'agnostic',
      }),
      field(t, 'curtailmentRiskTolerance', 'enum', {
        enumValues: ['low', 'medium', 'high'],
        derive: ({ projectIntent }) =>
          projectIntent?.priority === 'resilience' ? 'low' : 'medium',
      }),
      field(t, 'maxSubstationDistanceMiAmber', 'number', { unit: 'mi' }),
      field(t, 'maxSubstationDistanceMiRed', 'number', { unit: 'mi' }),
      field(t, 'timeToPowerCriticalDate', 'date'),
    ],
    'report-setup',
  );
}

function waterSetup(): ChecklistItem {
  const t = 'water-resource' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Water Resource report setup',
    'Heat-rejection sink (water-withdrawal axis), drought scenario, wastewater target, and water-rights diligence flags.',
    ['waterStress', 'environmental'],
    [
      // GAP 3 — surface the cold-END heat-rejection SINK as a derived, read-only
      // fact. This is the axis that drives water WITHDRAWAL (distinct from the
      // rack-side coolingStrategy), so the water-resource setup reflects the
      // real water demand driver. Derived from canonical intent → display-only.
      field(t, 'heatRejectionSink', 'enum', {
        label: 'Heat-rejection sink (water-withdrawal axis)',
        readOnly: true,
        enumValues: [
          'dry-cooler',
          'wet-cooling-tower',
          'hybrid',
          'once-through',
          'absorption-cchp',
          'free-cooling',
          'unknown',
        ],
        derive: ({ projectIntent }) => projectIntent?.bopHeatRejection,
      }),
      field(t, 'droughtScenario', 'enum', {
        enumValues: ['1-in-10', '1-in-100', 'both'],
        // GAP 3 — escalate the drought scenario when the declared sink WITHDRAWS
        // water (wet tower / once-through / hybrid). A water-withdrawing sink in
        // ANY region warrants the conservative scenario; otherwise fall back to
        // the region-stress heuristic.
        derive: ({ sed, projectIntent }) => {
          const sink = projectIntent?.bopHeatRejection;
          if (sink != null && WATER_WITHDRAWING_SINKS.has(sink)) return '1-in-100';
          return isHighWaterStressRegion(sed) ? '1-in-100' : '1-in-10';
        },
      }),
      field(t, 'wastewaterTarget', 'enum', {
        enumValues: ['npdes', 'zld', 'reuse'],
        // GAP 3 — a water-withdrawing sink under a high-water-stress region
        // points to zero-liquid-discharge as the conservative wastewater target.
        derive: ({ sed, projectIntent }) => {
          const sink = projectIntent?.bopHeatRejection;
          if (sink != null && WATER_WITHDRAWING_SINKS.has(sink) && isHighWaterStressRegion(sed)) {
            return 'zld';
          }
          return undefined;
        },
      }),
      field(t, 'waterRightsKnown', 'boolean'),
    ],
    'report-setup',
  );
}

function interconnectionSetup(): ChecklistItem {
  const t = 'interconnection' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Interconnection report setup',
    'Bandwidth, carrier diversity, latency targets, and peering flags.',
    ['connectivity'],
    [
      field(t, 'bandwidthGbps', 'number', {
        required: true,
        unit: 'Gbps',
        derive: ({ projectIntent }) => {
          const mw = projectIntent?.powerRequirementMW ?? 0;
          return mw > 100 ? 400 : 100;
        },
      }),
      field(t, 'carrierDiversityMin', 'enum', {
        enumValues: ['1', '2', '3'],
        derive: () => '2',
      }),
      field(t, 'latencyAwsMs', 'number', { unit: 'ms', label: 'Latency target — AWS (ms)' }),
      field(t, 'latencyAzureMs', 'number', { unit: 'ms', label: 'Latency target — Azure (ms)' }),
      field(t, 'latencyGcpMs', 'number', { unit: 'ms', label: 'Latency target — GCP (ms)' }),
      field(t, 'ixPeeringRequired', 'boolean'),
      field(t, 'submarineCableProximityImportant', 'boolean'),
    ],
    'report-setup',
  );
}

/**
 * P0-2 — Feasibility report setup. Replaces the old `kpiWeightSetup('feasibility')`
 * row of 5 raw KPI-weight numbers (`kpiGridWeight` …). Those weights:
 *   - permanently read "Not set" in the matrix (standard weights are represented
 *     by *absence* → scoringConfig default, so the field is canonically empty);
 *   - leaked `scoringConfig.json` internal taxonomy to users; and
 *   - dragged down the feasibility setup-row coverage fraction.
 * The weight knob stays where it belongs — the advanced Standard/Custom control
 * in `FeasibilityPreferencesForm` (it still flows to the scorer via
 * `typedPreferences.feasibility.kpiWeightOverrides`). This row instead surfaces
 * the decision-relevant BTM facts (all `derive()`-populated from the canonical
 * `projectIntent`, so they are READ-ONLY display facts, NOT new persisted prefs —
 * keeping the SSOT sentinel `checkNoSharedFieldsInPrefs.ts` happy).
 */
function feasibilitySetup(): ChecklistItem {
  const t = 'feasibility' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Feasibility report setup',
    'BTM decision facts — procurement strategy, firm-capacity basis, datacenter class, and carbon posture (derived from Site Project Intent).',
    ['siteProfile', 'grid'],
    [
      field(t, 'btmStrategy', 'enum', {
        label: 'BTM strategy',
        enumValues: ['grid-only', 'btm-only', 'hybrid', 'islanded'],
        // Nuclear interest alone says nothing about a grid connection.
        derive: ({ site }) => site?.typedPreferencesDefaults?.['power-availability']?.btmStrategy,
      }),
      field(t, 'firmCapacityBasisMW', 'number', {
        label: 'Firm-capacity basis (MW)',
        unit: 'MW',
        derive: ({ projectIntent }) => firmCapacityBasisFromIntent(projectIntent),
      }),
      field(t, 'datacenterTierTarget', 'enum', {
        label: 'Datacenter tier target',
        enumValues: ['I', 'II', 'III', 'IV'],
        derive: ({ projectIntent }) => projectIntent?.datacenterTierTarget,
      }),
      field(t, 'redundancyClass', 'enum', {
        label: 'Redundancy class',
        enumValues: ['N', 'N+1', '2N', '2N+1'],
        derive: ({ projectIntent }) => projectIntent?.redundancyClass,
      }),
      field(t, 'carbonCommitment', 'enum', {
        label: 'Carbon commitment',
        enumValues: ['none', 're100', '24-7-cfe', 'net-zero-2030', 'net-zero-2050', 'custom'],
        derive: ({ projectIntent }) => projectIntent?.carbonCommitment,
      }),
    ],
    'report-setup',
  );
}

/**
 * P0-3 — Supply Chain report setup. Replaces the fully-DEAD
 * `kpiWeightSetup('supply-chain-risk')` row whose weights wrote to
 * `typedPreferencesDefaults['supply-chain-risk'].kpiWeightOverrides` but were
 * read by NOTHING (the scorer only reads `.feasibility.kpiWeightOverrides`).
 * Surfaces the procurement-relevant facts instead — derived from intent so this
 * stays display-only (no new persisted prefs; SSOT-sentinel-safe).
 */
function supplyChainSetup(): ChecklistItem {
  const t = 'supply-chain-risk' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Supply Chain report setup',
    'Procurement posture — target energization, datacenter class, and redundancy driving long-lead equipment scope (derived from Site Project Intent).',
    ['siteProfile', 'grid'],
    [
      field(t, 'targetEnergizationDate', 'date', {
        label: 'Target energization date',
        derive: ({ projectIntent }) => projectIntent?.inServiceDate,
      }),
      field(t, 'firmCapacityBasisMW', 'number', {
        label: 'Firm-capacity basis (MW)',
        unit: 'MW',
        derive: ({ projectIntent }) => firmCapacityBasisFromIntent(projectIntent),
      }),
      field(t, 'datacenterTierTarget', 'enum', {
        label: 'Datacenter tier target',
        enumValues: ['I', 'II', 'III', 'IV'],
        derive: ({ projectIntent }) => projectIntent?.datacenterTierTarget,
      }),
      field(t, 'redundancyClass', 'enum', {
        label: 'Redundancy class',
        enumValues: ['N', 'N+1', '2N', '2N+1'],
        derive: ({ projectIntent }) => projectIntent?.redundancyClass,
      }),
    ],
    'report-setup',
  );
}

function customBriefSetup(): ChecklistItem {
  const t = 'custom-brief' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Custom Brief report setup',
    'Brief name, objective, diligence questions, and context.',
    ['siteResearch'],
    [
      field(t, 'briefName', 'string', {
        required: true,
        derive: ({ site }) => `${site.name} — Custom Brief`,
      }),
      field(t, 'objective', 'textarea'),
      field(t, 'questions', 'textarea', { required: true, help: 'One question per line.' }),
      field(t, 'contextNotes', 'textarea'),
      field(t, 'counterparties', 'string'),
      field(t, 'keyDates', 'string'),
    ],
    'report-setup',
  );
}

/**
 * FIN-1 (wf w7nuyquwd) — Financial Viability Report setup. The financial body
 * is computed LIVE from the deterministic project-finance engine driven by
 * `SiteEntity.financialAssumptions` (the SSOT — NOT captured here); these
 * fields only frame the verdict / pathway / tax presentation. `sedSectionsHelpful`
 * = grid/electricity (firm-generation cost basis) so the wizard surfaces the
 * economics evidence the engine reads.
 */
function financialSetup(): ChecklistItem {
  const t = 'financial' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Financial Viability report setup',
    'Verdict hurdle rate, pinned pathway comparison, and tax / PPA adjustment toggles. The financial model itself is driven by the site financial assumptions.',
    ['grid', 'electricity'],
    [
      field(t, 'hurdleRateOverride', 'number', {
        help: 'Target IRR the verdict bands against (e.g. 0.12 = 12%). Defaults to the engine WACC / discount rate.',
      }),
      field(t, 'pinnedPathwayComparisonId', 'string'),
      field(t, 'includeTaxCredits', 'boolean'),
      field(t, 'includePpaAdjustment', 'boolean'),
    ],
    'report-setup',
  );
}

function executiveSummarySetup(): ChecklistItem {
  const t = 'executive-summary' as const;
  return setupBase(
    reportSetupRequirementId(t),
    'Executive Summary report setup',
    'Source reports to synthesize and optional synthesis focus.',
    ['siteResearch'],
    [
      field(t, 'sourceReportIds', 'list', { required: true, label: 'Source reports' }),
      field(t, 'synthesisFocus', 'textarea'),
    ],
    'report-setup',
  );
}

const SETUP_BY_TYPE: Partial<Record<ReportTypeId, ChecklistItem>> = {
  'test-fit': testFitSetup(),
  environmental: environmentalSetup(),
  'power-availability': powerSetup(),
  'water-resource': waterSetup(),
  interconnection: interconnectionSetup(),
  feasibility: feasibilitySetup(),
  'supply-chain-risk': supplyChainSetup(),
  'custom-brief': customBriefSetup(),
  financial: financialSetup(),
  'executive-summary': executiveSummarySetup(),
};

/**
 * P3.8 — report types with NO wizard preferences step that still read the
 * site's Project Intent. Public Sentiment was invisible to every coverage
 * surface: excluded from `SETUP_ENABLED_REPORT_TYPES` (which promises a
 * per-type row), it contributed neither its own row nor the shared intent row.
 */
const INTENT_ROW_ONLY_REPORT_TYPES: ReadonlySet<ReportTypeId> = new Set<ReportTypeId>(['public-sentiment']);

/**
 * P3.8 follow-through (2026-09-05) — Public Sentiment's own per-type row. It has
 * no authored fields: the requirement is a completed, dated scan bound to the
 * current location and intent. An empty result supports coverage-only screening
 * (shared receipt admission resolved in
 * `lib/coverage/evidenceProbes.ts`). The row is evidence-probed, so the
 * coverage surfaces read the same verdict the chokepoint applies, and offer the
 * scan (`PsePrefetchCta`) as the action instead of a preferences form.
 */
export const PUBLIC_SENTIMENT_SCAN_REQUIREMENT_ID = reportSetupRequirementId('public-sentiment');

export function publicSentimentScanRow(): ChecklistItem {
  return {
    ...setupBase(
      PUBLIC_SENTIMENT_SCAN_REQUIREMENT_ID,
      'Public Sentiment scan',
      'A dated scan for the current project and location. Empty or unavailable sources produce a coverage-only screening with unknown stance.',
      ['publicSentiment'],
      [],
      'report-setup',
      'critical',
    ),
    // Resolved by `evidenceProbeFor` in lib/coverage/evidenceProbes.ts — config
    // stays data-only (see the ChecklistItem.evidenceProbeId note).
    evidenceProbeId: 'public-sentiment-scan',
  };
}

/** Site intent + per-type setup rows prepended to document checklists. */
export function getReportSetupChecklistFor(typeId: ReportTypeId): ChecklistItem[] {
  if (!isSetupEnabledReportType(typeId)) {
    if (typeId === 'public-sentiment') return [siteIntentRow(), publicSentimentScanRow()];
    return INTENT_ROW_ONLY_REPORT_TYPES.has(typeId) ? [siteIntentRow()] : [];
  }
  const setup = SETUP_BY_TYPE[typeId];
  if (!setup) return [siteIntentRow()];
  return [siteIntentRow(), setup];
}

/** All setup requirement ids (shared intent + per-type). */
export function allSetupRequirementIds(): string[] {
  const ids = new Set<string>([SITE_PROJECT_INTENT_ID, PUBLIC_SENTIMENT_SCAN_REQUIREMENT_ID]);
  for (const t of SETUP_ENABLED_REPORT_TYPES) {
    ids.add(reportSetupRequirementId(t));
  }
  return [...ids];
}
